import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useAdminConfigHistory } from '../../../api/admin-config';
import { formatValue } from '../_lib/configDisplay';
import { shortActor } from '../_lib/formatActor';

/**
 * Lazy per-key change history for an expanded config row (AUT-131,
 * architect guardrail #6). The `useAdminConfigHistory(key, enabled)` query
 * fires ONLY while the row is expanded — never prefetched for all 44 keys.
 *
 * Entries arrive newest-first from the backend (`changedAt DESC`); we
 * render the `old → new` diff + the raw actor id (truncated) + the date.
 * No backend name resolution (out of scope) — `changedBy` is a raw admin
 * id; `—` when null (seed / system write).
 */
export interface ConfigHistoryProps {
  configKey: string;
  /** Drives the lazy fetch — true once the row is expanded. */
  expanded: boolean;
}

export const ConfigHistory = ({ configKey, expanded }: ConfigHistoryProps): ReactElement => {
  const { t } = useTranslation();
  const query = useAdminConfigHistory(configKey, expanded);

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          {t('config.history.loading')}
        </Typography>
      </Box>
    );
  }

  if (query.error) {
    return (
      <Alert severity="error" role="alert" sx={{ my: 1 }}>
        {t('config.history.error')}
      </Alert>
    );
  }

  const entries = query.data?.entries ?? [];
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        {t('config.history.empty')}
      </Typography>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('config.history.title')}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'grid', gap: 0.75 }}>
        {entries.map((entry, index) => (
          <Box
            component="li"
            key={`${entry.changedAt}-${index}`}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'baseline' }}
          >
            <Typography
              component="span"
              variant="body2"
              sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
            >
              {formatValue(entry.oldValue)}
            </Typography>
            <Typography component="span" variant="body2" color="text.secondary">
              →
            </Typography>
            <Typography
              component="span"
              variant="body2"
              sx={{ fontFamily: 'monospace', fontWeight: 600 }}
            >
              {formatValue(entry.newValue)}
            </Typography>
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 'auto' }}
            >
              {new Date(entry.changedAt).toLocaleDateString('ru-RU')} ·{' '}
              {shortActor(entry.changedBy) ?? t('config.noValue')}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
